import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Check, CheckCheck, X, ChevronLeft, ChevronRight } from 'lucide-react';

const URL_REGEX = /(https?:\/\/[^\s<>"{}|\\^`[\]]+)/g;

function renderContentWithLinks(content, isOwn) {
  if (!content) return null;
  const parts = content.split(URL_REGEX);
  return parts.map((part, i) =>
    part.match(/^https?:\/\//)
      ? <a key={i} href={part} target="_blank" rel="noopener noreferrer"
           className={`underline break-all ${isOwn ? 'text-blue-200 hover:text-blue-100' : 'text-blue-600 hover:text-blue-800'}`}>{part}</a>
      : <span key={i}>{part}</span>
  );
}

function ImageGrid({ images, onImageClick }) {
  if (!images || images.length === 0) return null;

  if (images.length === 1) {
    return (
      <div className="mt-1 mb-1">
        <img
          src={images[0].url}
          alt={images[0].name || 'Image'}
          className="rounded-lg max-w-full max-h-64 cursor-pointer object-cover"
          onClick={() => onImageClick(0)}
          loading="lazy"
        />
      </div>
    );
  }

  if (images.length === 2) {
    return (
      <div className="mt-1 mb-1 grid grid-cols-2 gap-1">
        {images.map((img, i) => (
          <img
            key={i}
            src={img.url}
            alt={img.name || 'Image'}
            className="rounded-lg w-full h-32 object-cover cursor-pointer"
            onClick={() => onImageClick(i)}
            loading="lazy"
          />
        ))}
      </div>
    );
  }

  return (
    <div className="mt-1 mb-1 grid grid-cols-2 gap-1">
      {images.map((img, i) => (
        <img
          key={i}
          src={img.url}
          alt={img.name || 'Image'}
          className={`rounded-lg w-full h-32 object-cover cursor-pointer ${
            images.length % 2 === 1 && i === images.length - 1 ? 'col-span-2 h-40' : ''
          }`}
          onClick={() => onImageClick(i)}
          loading="lazy"
        />
      ))}
    </div>
  );
}

function ImageLightbox({ images, initialIndex, onClose }) {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') setCurrentIndex(i => Math.max(0, i - 1));
      if (e.key === 'ArrowRight') setCurrentIndex(i => Math.min(images.length - 1, i + 1));
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [images.length, onClose]);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  return createPortal(
    <div
      className="fixed inset-0 bg-black/90 flex items-center justify-center"
      style={{ zIndex: 10000 }}
      onClick={onClose}
    >
      <button
        className="absolute top-4 right-4 text-white/80 hover:text-white z-10"
        onClick={onClose}
      >
        <X className="h-6 w-6" />
      </button>

      {images.length > 1 && (
        <div className="absolute top-4 left-1/2 -translate-x-1/2 text-white/80 text-sm">
          {currentIndex + 1} / {images.length}
        </div>
      )}

      {currentIndex > 0 && (
        <button
          className="absolute left-4 top-1/2 -translate-y-1/2 text-white/80 hover:text-white"
          onClick={(e) => { e.stopPropagation(); setCurrentIndex(i => i - 1); }}
        >
          <ChevronLeft className="h-8 w-8" />
        </button>
      )}

      <img
        src={images[currentIndex].url}
        alt={images[currentIndex].name || 'Image'}
        className="max-w-[90vw] max-h-[90vh] object-contain"
        onClick={(e) => e.stopPropagation()}
      />

      {currentIndex < images.length - 1 && (
        <button
          className="absolute right-4 top-1/2 -translate-y-1/2 text-white/80 hover:text-white"
          onClick={(e) => { e.stopPropagation(); setCurrentIndex(i => i + 1); }}
        >
          <ChevronRight className="h-8 w-8" />
        </button>
      )}
    </div>,
    document.body
  );
}

function MessageBubble({ message, isOwn }) {
  const [lightboxIndex, setLightboxIndex] = useState(null);

  const formatTime = (date) => {
    if (!date) return '';
    const d = date instanceof Date ? date : new Date(date);
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className={`flex ${isOwn ? 'justify-end' : 'justify-start'} mb-3`}>
      <div
        className={`max-w-[85%] sm:max-w-[70%] rounded-2xl px-4 py-2 ${
          isOwn
            ? 'bg-blue-600 text-white rounded-br-md'
            : 'bg-white border border-slate-200 text-slate-900 rounded-bl-md shadow-sm'
        }`}
      >
        {!isOwn && message.senderEmail && (
          <p className="text-xs text-slate-500 mb-1">{message.senderEmail}</p>
        )}
        {message.images && message.images.length > 0 && (
          <ImageGrid images={message.images} onImageClick={(i) => setLightboxIndex(i)} />
        )}
        {message.content && (
          <p className="text-sm whitespace-pre-wrap break-words">
            {renderContentWithLinks(message.content, isOwn)}
          </p>
        )}
        <div className={`flex items-center gap-1 mt-1 ${isOwn ? 'justify-end' : 'justify-start'}`}>
          <span className={`text-xs ${isOwn ? 'text-blue-200' : 'text-slate-500'}`}>
            {formatTime(message.createdAt)}
          </span>
          {isOwn && (
            message.readAt ? (
              <CheckCheck className="h-3 w-3 text-blue-200" />
            ) : (
              <Check className="h-3 w-3 text-blue-300" />
            )
          )}
        </div>
      </div>
      {lightboxIndex !== null && message.images && (
        <ImageLightbox
          images={message.images}
          initialIndex={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
        />
      )}
    </div>
  );
}

export function DateDivider({ date }) {
  const formatDate = (date) => {
    if (!date) return '';
    const d = date instanceof Date ? date : new Date(date);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (d.toDateString() === today.toDateString()) {
      return 'Today';
    } else if (d.toDateString() === yesterday.toDateString()) {
      return 'Yesterday';
    } else {
      return d.toLocaleDateString([], { weekday: 'long', month: 'long', day: 'numeric' });
    }
  };

  return (
    <div className="flex items-center justify-center my-4">
      <span className="text-xs text-slate-500 bg-slate-100 px-3 py-1 rounded-full">
        {formatDate(date)}
      </span>
    </div>
  );
}

export default MessageBubble;
